/**
 * Jahresabweichung vom Referenzmittel (Konvention der Klimaprodukte von
 * MeteoSchweiz): Balken ab der Mittellinie, rot = wärmeres Jahr, blau =
 * kühleres Jahr. Bei Kennzahlen, deren höherer Wert ein kühleres Jahr
 * bedeutet (Heizgradtage, Kühlstunden), kehrt sich die Farbzuordnung um.
 * Teiljahre erhalten keine Abweichung (Teilsummen sind nicht vergleichbar).
 */
import { useMemo, useState, type KeyboardEvent } from 'react';
import { scaleBand, scaleLinear } from 'd3-scale';
import type { AnalysisConfig, MetricKey, ReferenceSummary, YearMetrics } from '../../core/domain/types';
import { METRICS } from '../../app/metrics';
import { fmt, fmtSigned } from '../../app/format';
import { useWidth } from '../hooks';
import { Patterns, Tooltip, niceTicks, useTip } from './common';
import { yearStatus, type YearStatus } from './YearChart';

export const WARM_HEX = '#c8202a';
export const COOL_HEX = '#1c63a6';

interface Props {
  years: YearMetrics[];
  metric: MetricKey;
  cfg: AnalysisConfig;
  reference: ReferenceSummary;
  from: number;
  to: number;
  selectedYear?: number | null;
  onSelectYear?: (y: number) => void;
  height?: number;
  fixedWidth?: number;
  idPrefix?: string;
}

export function AnomalyChart({ years, metric, cfg, reference, from, to, selectedYear, onSelectYear, height = 320, fixedWidth, idPrefix = 'ac' }: Props) {
  const [ref, measured] = useWidth<HTMLDivElement>(800);
  const width = fixedWidth ?? measured;
  const meta = METRICS[metric];
  const refMean = reference.means[metric] ?? 0;
  const { tip, show, hide } = useTip();
  const [focusYear, setFocusYear] = useState<number | null>(null);
  const narrow = width < 520;
  const dec = meta.kind === 'mean' ? 1 : meta.refDecimals;

  const data = useMemo(() => {
    const out: { year: number; m?: YearMetrics; v: number | null; a: number | null; status: YearStatus }[] = [];
    for (let y = from; y <= to; y++) {
      const m = years.find((x) => x.year === y);
      const status = yearStatus(m);
      const v = m && (status === 'vollständig' || status === 'Teilwert') ? (m[metric] as number) : null;
      out.push({ year: y, m, v, a: status === 'vollständig' && v !== null ? v - refMean : null, status });
    }
    return out;
  }, [years, metric, from, to, refMean]);

  const margin = { top: 28, right: narrow ? 12 : 150, bottom: 28, left: narrow ? 44 : 56 };
  const iw = Math.max(80, width - margin.left - margin.right);
  const ih = height - margin.top - margin.bottom;
  const x = scaleBand<number>()
    .domain(data.map((d) => d.year))
    .range([0, iw])
    .paddingInner(data.length > 25 ? 0.22 : 0.3)
    .paddingOuter(0.1);
  const maxAbs = Math.max(...data.map((d) => Math.abs(d.a ?? 0)), meta.kind === 'mean' ? 0.5 : 1);
  // Symmetrische Skala, deren Grenze sicher über dem grössten Betrag liegt
  const t0 = niceTicks(0, maxAbs * 1.05, narrow ? 2 : 3);
  const step = t0.length > 1 ? t0[1] - t0[0] : maxAbs;
  const lim = Math.ceil((maxAbs * 1.05) / step) * step;
  const y = scaleLinear().domain([-lim, lim]).range([ih, 0]);
  const ticks: number[] = [];
  for (let v = -lim; v <= lim + step * 1e-9; v += step) ticks.push(Math.round(v / step) * step);
  const labelEvery = x.bandwidth() < 14 ? (narrow ? 10 : 5) : x.bandwidth() < 24 ? 5 : 1;
  const refX0 = x(Math.max(from, cfg.referenceStart));
  const refX1 = x(Math.min(to, cfg.referenceEnd));
  const refVisible = refX0 !== undefined && refX1 !== undefined && cfg.referenceStart <= to && cfg.referenceEnd >= from;
  const colorOf = (a: number) => (a * meta.warmSense >= 0 ? WARM_HEX : COOL_HEX);
  const senseText = (a: number) => (a === 0 ? 'wie das Mittel' : a * meta.warmSense > 0 ? 'wärmer als das Mittel' : 'kühler als das Mittel');

  const tipFor = (d: (typeof data)[number]) => (
    <>
      <strong>{d.year}</strong>
      <br />
      {meta.label}: {d.v === null ? '–' : `${fmt(d.v, meta.decimals)} ${meta.unit}`}
      <br />
      {d.a !== null ? (
        <>
          Abweichung: {fmtSigned(d.a, dec)} {meta.unit} ({senseText(d.a)})
        </>
      ) : (
        <span className="tt-status">{d.status === 'Teilwert' ? 'Teiljahr: keine Abweichung berechnet' : `Status: ${d.status}`}</span>
      )}
    </>
  );

  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    const cur = focusYear ?? selectedYear ?? to;
    let next = cur;
    if (e.key === 'ArrowRight') next = Math.min(to, cur + 1);
    else if (e.key === 'ArrowLeft') next = Math.max(from, cur - 1);
    else if (e.key === 'Home') next = from;
    else if (e.key === 'End') next = to;
    else if ((e.key === 'Enter' || e.key === ' ') && onSelectYear) {
      e.preventDefault();
      onSelectYear(cur);
      return;
    } else return;
    e.preventDefault();
    setFocusYear(next);
    const d = data.find((q) => q.year === next);
    if (d) show({ x: margin.left + (x(next) ?? 0) + x.bandwidth() / 2, y: margin.top + (d.a !== null ? y(d.a) : ih / 2) - 40, content: tipFor(d) });
  };

  const counted = data.filter((d) => d.a !== null);
  const summary = `${meta.label} ${from}–${to}, Abweichung vom Mittel ${reference.start}–${reference.end} (${fmt(refMean, meta.refDecimals)} ${meta.unit}). ${counted.filter((d) => d.a! * meta.warmSense > 0).length} wärmere und ${counted.filter((d) => d.a! * meta.warmSense < 0).length} kühlere Jahre. Pfeiltasten wählen ein Jahr, Eingabetaste öffnet das Tagesdetail.`;

  return (
    <div ref={ref} className="chart" style={{ position: 'relative', width: fixedWidth }}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={summary}
        tabIndex={0}
        onKeyDown={onKey}
        onBlur={() => {
          setFocusYear(null);
          hide();
        }}
      >
        <Patterns id={idPrefix} />
        <g transform={`translate(${margin.left},${margin.top})`}>
          {refVisible && (
            <g>
              <rect x={(refX0 ?? 0) - x.step() * x.paddingInner() * 0.5} y={-margin.top + 6} width={(refX1 ?? 0) - (refX0 ?? 0) + x.bandwidth() + x.step() * x.paddingInner()} height={ih + margin.top - 6} fill="#f3f4f6" />
              <text x={(refX0 ?? 0) + 2} y={-margin.top + 20} fontSize={12} fill="#4b5563">
                Referenzperiode {cfg.referenceStart}–{cfg.referenceEnd}
              </text>
            </g>
          )}
          <g className="axis">
            {ticks.map((t) => (
              <g key={t} transform={`translate(0,${y(t)})`}>
                <line className="gridline" x1={0} x2={iw} />
                <text x={-8} dy="0.32em" textAnchor="end">
                  {t === 0 ? '0' : fmtSigned(t, meta.kind === 'mean' ? 1 : 0)}
                </text>
              </g>
            ))}
            <text x={-margin.left + 2} y={-12} fontSize={12} fill="#4b5563">
              {meta.unit}
            </text>
          </g>
          {data.map((d) => {
            const bx = x(d.year) ?? 0;
            const bw = x.bandwidth();
            const active = d.year === selectedYear || d.year === focusYear;
            const common = {
              onMouseMove: (e: React.MouseEvent) => {
                const r = (e.currentTarget as SVGElement).ownerSVGElement!.getBoundingClientRect();
                show({ x: e.clientX - r.left, y: e.clientY - r.top - 50, content: tipFor(d) });
              },
              onMouseLeave: hide,
              onClick: () => onSelectYear?.(d.year),
            };
            if (d.a === null) {
              return (
                <g key={d.year} {...common} className="bar-hit">
                  <rect x={bx} y={0} width={bw} height={ih} fill="transparent" />
                  {d.status === 'lädt' ? (
                    <rect x={bx} y={y(0) - 1.5} width={bw} height={3} fill="#d1d5db" />
                  ) : (
                    <rect x={bx} y={y(0) - 10} width={bw} height={20} fill={`url(#${idPrefix}-hatch)`} stroke="#9ca3af" strokeWidth={1} />
                  )}
                </g>
              );
            }
            const y0 = y(0);
            const y1 = y(d.a);
            const top = Math.min(y0, y1);
            const h = Math.max(1, Math.abs(y1 - y0));
            return (
              <g key={d.year} {...common} className="bar-hit">
                <rect x={bx} y={0} width={bw} height={ih} fill="transparent" />
                <rect x={bx} y={top} width={bw} height={h} fill={colorOf(d.a)} opacity={active ? 1 : 0.9} />
                {active && <rect x={bx - 2} y={top - 2} width={bw + 4} height={h + 4} fill="none" stroke="#006699" strokeWidth={2} />}
              </g>
            );
          })}
          <line x1={0} x2={iw} y1={y(0)} y2={y(0)} stroke="#1f2937" strokeWidth={1.4} />
          {narrow ? (
            <text className="ref-label" x={iw} y={y(lim) + 12} textAnchor="end" fill="#1f2937" stroke="#fff" strokeWidth={4} paintOrder="stroke">
              0 = Mittel {reference.start}–{reference.end}: {fmt(refMean, meta.refDecimals)}
            </text>
          ) : (
            <>
              <text className="ref-label" x={iw + 10} y={y(0) - 3} fill="#1f2937">
                Mittel {reference.start}–{reference.end}
              </text>
              <text x={iw + 10} y={y(0) + 13} fontSize={12} fill="#1f2937">
                {fmt(refMean, meta.refDecimals)} {meta.unit}
                {reference.status === 'teil' ? ` · Teilreferenz` : ''}
              </text>
              <text x={iw + 10} y={y(lim) + 10} fontSize={12} fill={meta.warmSense > 0 ? WARM_HEX : COOL_HEX} fontWeight={700}>
                {meta.warmSense > 0 ? '▲ wärmer' : '▲ kühler'}
              </text>
              <text x={iw + 10} y={y(-lim) - 2} fontSize={12} fill={meta.warmSense > 0 ? COOL_HEX : WARM_HEX} fontWeight={700}>
                {meta.warmSense > 0 ? '▼ kühler' : '▼ wärmer'}
              </text>
            </>
          )}
          <g className="axis" transform={`translate(0,${ih})`}>
            {data
              .filter((d) => d.year % labelEvery === 0 || labelEvery === 1)
              .map((d) => (
                <text key={d.year} x={(x(d.year) ?? 0) + x.bandwidth() / 2} y={18} textAnchor="middle">
                  {labelEvery === 1 && x.bandwidth() < 34 ? String(d.year).slice(2) : d.year}
                </text>
              ))}
          </g>
        </g>
      </svg>
      <Tooltip tip={tip} width={width} />
    </div>
  );
}

export function AnomalyLegend() {
  return (
    <div className="chart-legend">
      <span className="legend-item">
        <span className="legend-swatch" style={{ background: WARM_HEX }} /> wärmer als das Mittel
      </span>
      <span className="legend-item">
        <span className="legend-swatch" style={{ background: COOL_HEX }} /> kühler als das Mittel
      </span>
      <span className="legend-item">
        <span className="legend-swatch" style={{ background: 'repeating-linear-gradient(45deg,#9ca3af 0 1.5px,#fff 1.5px 4px)', border: '1px solid #9ca3af' }} /> Teiljahr (keine Abweichung)
      </span>
      <span className="legend-item">
        <span className="legend-swatch" style={{ background: '#d1d5db', height: 3 }} /> noch nicht geladen
      </span>
    </div>
  );
}
